import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import './Login.css';

const SsoCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { loginWithMicrosoftCode } = useAuth();
  const { showError } = useNotification();
  const [statusMessage, setStatusMessage] = useState('Completing Microsoft sign-in...');
  const hasStartedRef = useRef(false);
  const redirectTimerRef = useRef(null);

  const redirectToLoginWithMessage = useCallback((message) => {
    setStatusMessage(message);
    showError(message);

    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
    }

    redirectTimerRef.current = setTimeout(() => {
      navigate('/login', { replace: true });
    }, 2000);
  }, [navigate, showError]);

  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const error = params.get('error');
    const message = params.get('message');

    if (error) {
      const errorMessage = message || 'Microsoft sign-in was cancelled or failed.';
      redirectToLoginWithMessage(errorMessage);
      return;
    }

    if (!code) {
      const errorMessage = 'Invalid callback request. Please sign in again.';
      redirectToLoginWithMessage(errorMessage);
      return;
    }

    const exchangeCode = async () => {
      const result = await loginWithMicrosoftCode(code);
      if (result && result.success === true) {
        navigate('/', { replace: true });
        return;
      }

      const errorMessage =
        result?.error ||
        result?.errorData?.message ||
        'Microsoft sign-in failed. Please try again.';
      redirectToLoginWithMessage(errorMessage);
    };

    exchangeCode();
  }, [location.search, loginWithMicrosoftCode, redirectToLoginWithMessage, navigate]);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="login-container">
      <div className="login-wrapper">
        <div className="login-form-section" style={{ flex: 1 }}>
          <div className="login-card" role="status" aria-live="polite">
            <div className="login-header">
              <h2>Microsoft Sign-in</h2>
              <p>{statusMessage}</p>
            </div>
            <button
              type="button"
              className="login-button"
              onClick={() => navigate('/login', { replace: true })}
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SsoCallback;
